import {IconButton, openDialog, Tooltip} from 'platform/components';
import {HStack, Show} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useState} from 'react';

import {always} from 'ramda';

import {useGetParticipationQuery} from '@dms/api/core';
import {
  AdvertisementStatusEnum,
  useGetVehicleQuery,
  useRepeatLastActionMutation,
  useUpdateVehicleOnPlatformMutation,
} from '@dms/api/sales';
import {AdvertisingPlatformCodeEnum, EntityResourceIds} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {handleApiError, usePermissions} from '@dms/shared';

import {useAdvertisementPolling} from '../contexts/AdvertisementPollingContext';
import {PlatformEdit} from './PlatformEdit';

interface ActionsProps {
  vehicleId: string;
  platformCode: AdvertisingPlatformCodeEnum;
  currentStatus: AdvertisementStatusEnum;
  platformTitle: string;
  inSale: boolean;
  statusTriggeredAt: string | null;
}

export const PlatformActions = ({
  vehicleId,
  platformCode,
  currentStatus,
  platformTitle,
  inSale,
  statusTriggeredAt,
}: ActionsProps) => {
  const [updateVehicleOnPlatform] = useUpdateVehicleOnPlatformMutation();
  const [repeatLastAction] = useRepeatLastActionMutation();
  const {resetPollingInterval} = useAdvertisementPolling();
  const [clickedAt, setClickedAt] = useState<string | null | undefined>(undefined);

  const {data: vehicleParticipation} = useGetParticipationQuery({
    resourceId: EntityResourceIds.vehicle,
    recordId: vehicleId,
  });

  const {data: vehicle} = useGetVehicleQuery({vehicleId});

  const [
    hasEditPlatformSettingsPermission,
    hasRepeatLastPublishingActionPermission,
    hasUpdateVehiclePlatformPermission,
  ] = usePermissions({
    permissionKeys: ['editPlatformSettings', 'repeatLastPublishingAction', 'updateVehiclePlatform'],
    scopes: {
      editPlatformSettings: {
        participation: vehicleParticipation,
        branchId: vehicle?.branchId,
      },
      repeatLastPublishingAction: {
        participation: vehicleParticipation,
        branchId: vehicle?.branchId,
      },
      updateVehiclePlatform: {
        participation: vehicleParticipation,
        branchId: vehicle?.branchId,
      },
    },
  });

  // Prevent BE errors when interval between multiple triggers is less than approx. 5s
  const preventMultipleTriggers = clickedAt === statusTriggeredAt;

  const handleUpdate = () => {
    resetPollingInterval();
    setClickedAt(statusTriggeredAt);
    updateVehicleOnPlatform({vehicleId, platformCode}).unwrap().catch(handleApiError);
  };

  const handleRepeatLastAction = () => {
    resetPollingInterval();
    setClickedAt(statusTriggeredAt);
    repeatLastAction({vehicleId, platformCode}).unwrap().catch(handleApiError);
  };

  const handleOpenDetail = () => {
    openDialog(
      <PlatformEdit
        vehicleId={vehicleId}
        platformCode={platformCode}
        isDisabled={!hasEditPlatformSettingsPermission}
      />,
      {
        title: i18n.t('entity.vehicle.labels.listingAttributes') + ` "${platformTitle}"`,
      }
    );
  };

  const showUpdateAction = currentStatus === AdvertisementStatusEnum.PUBLISHED;

  const showRepeatAction = match(currentStatus)
    .with(
      AdvertisementStatusEnum.PUBLISH_FAILED,
      AdvertisementStatusEnum.UNPUBLISH_FAILED,
      AdvertisementStatusEnum.UPDATE_FAILED,
      always(true)
    )
    .otherwise(always(false));

  return (
    <HStack align="center" justify="flex-end">
      <Show when={hasRepeatLastPublishingActionPermission && showRepeatAction && inSale}>
        <Tooltip label={i18n.t('page.advertisement.actions.repeatLastAction')}>
          <IconButton
            icon="AV/replay"
            onClick={handleRepeatLastAction}
            data-testid={`${platformCode}-repeat-last-action`}
            isDisabled={preventMultipleTriggers}
          />
        </Tooltip>
      </Show>

      <Show when={hasUpdateVehiclePlatformPermission && showUpdateAction && inSale}>
        <Tooltip label={i18n.t('page.advertisement.actions.updateAd')}>
          <IconButton
            icon="action/update"
            onClick={handleUpdate}
            data-testid={`${platformCode}-update-ad`}
            isDisabled={preventMultipleTriggers}
          />
        </Tooltip>
      </Show>

      <Tooltip label={`${i18n.t('page.advertisement.actions.editPlatform')} ${platformTitle}`}>
        <IconButton
          icon="navigation/chevron_right"
          onClick={handleOpenDetail}
          data-testid={`link-advertisement-platforms.${platformCode}`}
        />
      </Tooltip>
    </HStack>
  );
};
